import { z } from 'zod';
import { STORE_CATEGORIES } from '../catalog.ts';
import { SHOPPING_CHECK_STATES, SHOPPING_RANGE_MODES } from '../planning.ts';
import { isoDateSchema, timeOfDaySchema } from './planning.ts';
import { unitSchema } from './units.ts';

const text = (max: number) => z.string().trim().min(1).max(max);

/**
 * Fields an outbox op may set per entity (PLAN.md §9, ADR 0006). An upsert of a new row must carry
 * the `required` ones; an upsert of an existing row changes only the fields it names.
 */
export const syncFieldSchemas = {
  cookSession: z
    .object({
      recipeId: z.uuid(),
      date: isoDateSchema,
      portions: z.number().int().min(1).max(100),
      doneAt: z.iso.datetime().nullable(),
      notes: z.string().trim().max(1000).nullable(),
      imageId: z.uuid().nullable(),
    })
    .partial(),
  planEntry: z
    .object({
      date: isoDateSchema,
      slotKey: text(40),
      userId: z.uuid().nullable(),
      cookSessionId: z.uuid().nullable(),
      freeText: text(200).nullable(),
      portions: z.number().positive().max(100),
    })
    .partial(),
  mealSlot: z
    .object({
      key: text(40),
      label: text(40).nullable(),
      position: z.number().int().min(0).max(100),
      defaultTime: timeOfDaySchema.nullable(),
      visible: z.boolean(),
    })
    .partial(),
  /** Id: `shoppingCheckId(rangeStart, itemKey)`; delete = uncheck. */
  shoppingCheck: z.object({ state: z.enum(SHOPPING_CHECK_STATES) }).partial(),
  manualItem: z
    .object({
      text: text(200),
      quantity: z.number().nonnegative().max(100_000).nullable(),
      unit: unitSchema.nullable(),
      ingredientId: z.uuid().nullable(),
      category: z.enum(STORE_CATEGORIES),
      checkedAt: z.iso.datetime().nullable(),
    })
    .partial(),
  /** A recipe on the shopping list without a day; delete = off the list. */
  shoppingRecipe: z
    .object({
      recipeId: z.uuid(),
      portions: z.number().int().min(1).max(100),
    })
    .partial(),
  /** Id: `household` (one row per household); never deleted. */
  shoppingPrefs: z
    .object({
      rangeMode: z.enum(SHOPPING_RANGE_MODES),
      rangeDays: z.number().int().min(1).max(28),
      shoppingWeekday: z.literal([1, 2, 3, 4, 5, 6, 7]),
    })
    .partial(),
  /** Id: the catalog ingredient's id (the household's `household_ingredient` row); never deleted. */
  staple: z
    .object({
      /** Null = the catalog default. */
      isStaple: z.boolean().nullable(),
      runningLow: z.boolean(),
    })
    .partial(),
} as const;

export type SyncEntity = keyof typeof syncFieldSchemas;
export const SYNC_ENTITIES = Object.keys(syncFieldSchemas) as SyncEntity[];
export type SyncFields<E extends SyncEntity> = z.infer<(typeof syncFieldSchemas)[E]>;

/** Fields a new row needs. */
export const REQUIRED_FIELDS: { [E in SyncEntity]: readonly (keyof SyncFields<E>)[] } = {
  cookSession: ['recipeId', 'date', 'portions'],
  planEntry: ['date', 'slotKey'],
  mealSlot: ['key', 'position'],
  shoppingCheck: [],
  manualItem: ['text'],
  shoppingRecipe: ['recipeId', 'portions'],
  shoppingPrefs: [],
  staple: [],
};

const opBase = {
  /** Client-generated; applying the same op twice changes nothing. */
  opId: z.uuid(),
  op: z.enum(['upsert', 'delete']),
  id: z.string().min(1).max(300),
  /** When the change was made on the device: the later write to a row wins. */
  clientTs: z.iso.datetime(),
};

const opFor = <E extends SyncEntity, S extends z.ZodType>(entity: E, fields: S) =>
  z.object({ ...opBase, entity: z.literal(entity), fields });

const f = syncFieldSchemas;
/** One outbox op: a generic, idempotent write (PLAN.md §9). */
export const syncOpSchema = z.discriminatedUnion('entity', [
  opFor('cookSession', f.cookSession),
  opFor('planEntry', f.planEntry),
  opFor('mealSlot', f.mealSlot),
  opFor('shoppingCheck', f.shoppingCheck),
  opFor('manualItem', f.manualItem),
  opFor('shoppingRecipe', f.shoppingRecipe),
  opFor('shoppingPrefs', f.shoppingPrefs),
  opFor('staple', f.staple),
]);
export type SyncOp = z.infer<typeof syncOpSchema>;

export const syncPushSchema = z.object({ ops: z.array(syncOpSchema).min(1).max(200) });

/**
 * - `applied`: written. `duplicate`: this op id was applied before (nothing changed).
 * - `stale`: the row changed later on another device; the pull brings the newer state.
 * - `rejected`: invalid (unknown row, missing fields, another household's reference).
 */
export const SYNC_OP_STATUSES = ['applied', 'duplicate', 'stale', 'rejected'] as const;
export type SyncOpStatus = (typeof SYNC_OP_STATUSES)[number];

export const syncPushResponseSchema = z.object({
  results: z.array(
    z.object({
      opId: z.uuid(),
      status: z.enum(SYNC_OP_STATUSES),
      error: z.string().optional(),
    }),
  ),
});
export type SyncPushResponse = z.infer<typeof syncPushResponseSchema>;
